'use client';

import { Badge, BodyDefault, MetaDefault } from '@terra/ui-design-system';
import {
  Box,
  HStack,
  VStack,
  token,
  type Token,
} from '@terra/ui-styled-system';
import { format } from 'date-fns';
import { useTranslations } from 'next-intl';
import type { CrawlerSessionStatusType } from '../../hooks/useCrawlerSessions';
import {
  formatChangeSummary,
  formatRelativeCrawlTime,
  historyStatusLabelKey,
  triggerLabelKey,
  type HistorySessionWithMeta,
} from './crawlerHistory.utils';

const SESSION_STATUS_DOT_COLOR: Record<CrawlerSessionStatusType, Token> = {
  RUNNING: 'colors.fg.accent.default',
  ABORTING: 'colors.fg.severity.medium',
  COMPLETED: 'colors.fg.status.resolved',
  FAILED: 'colors.fg.status.unresolved',
  ABORTED: 'colors.fg.severity.high',
};

type CrawlerHistorySessionRowProps = {
  session: HistorySessionWithMeta;
  nowMs: number;
};

export function CrawlerHistorySessionRow({
  session,
  nowMs,
}: CrawlerHistorySessionRowProps) {
  const t = useTranslations('crawler.historyDrawer');
  const tCommon = useTranslations('common');
  const statusKey = historyStatusLabelKey(session.status);
  const triggerKey = triggerLabelKey(session.crawlerType);
  const changes = formatChangeSummary({ stats: session.stats });
  const statusLabel =
    statusKey === 'completed'
      ? t('statusCompleted')
      : statusKey === 'stopped'
        ? t('statusStopped')
        : statusKey === 'failed'
          ? t('statusFailed')
          : t('statusRunning');

  const relative = formatRelativeCrawlTime({
    date: session.finishedAt ?? session.createdAt,
    now: new Date(nowMs),
  });
  const timeLabel =
    relative?.kind === 'justNow'
      ? t('justNow')
      : format(new Date(session.createdAt), 'h:mm a');

  return (
    <VStack
      w="full"
      gap="1"
      alignItems="stretch"
      px="2"
      py="2"
      borderBottomWidth="1px"
      borderColor="border.muted"
    >
      <HStack
        w="full"
        justifyContent="space-between"
        alignItems="center"
        gap="2"
      >
        <HStack gap="2" alignItems="center" minW="0" flex="1">
          <BodyDefault truncate flexShrink={1} minW="0">
            {session.credentialName}
          </BodyDefault>
          {session.credentialRole ? (
            <Badge variant="outline" size="sm" flexShrink={0}>
              {session.credentialRole}
            </Badge>
          ) : !session.credentialId ? (
            <MetaDefault color="fg.subtle" flexShrink={0}>
              {t('noAuth')}
            </MetaDefault>
          ) : null}
        </HStack>
        <HStack gap="1.5" alignItems="center" flexShrink={0}>
          <Box
            w="1.5"
            h="1.5"
            borderRadius="full"
            flexShrink={0}
            style={{
              backgroundColor: token(SESSION_STATUS_DOT_COLOR[session.status]),
            }}
          />
          <MetaDefault color="fg.subtle">{statusLabel}</MetaDefault>
        </HStack>
      </HStack>

      <HStack
        w="full"
        justifyContent="space-between"
        alignItems="center"
        gap="2"
      >
        <HStack gap="2" alignItems="center" minW="0">
          <MetaDefault color="fg.muted">
            {triggerKey === 'manual' ? t('triggerManual') : t('triggerCrawler')}
          </MetaDefault>
          <MetaDefault color="fg.muted">{timeLabel}</MetaDefault>
        </HStack>
        <HStack gap="2" alignItems="center" flexShrink={0}>
          {changes.length === 0 ? (
            <MetaDefault color="fg.muted">{tCommon('noChanges')}</MetaDefault>
          ) : (
            changes.map((change) => (
              <MetaDefault key={change.kind}>
                {change.kind === 'endpoints'
                  ? t('endpointsAdded', { count: change.count })
                  : change.count === 1
                    ? t('pageAdded', { count: change.count })
                    : t('pagesAdded', { count: change.count })}
              </MetaDefault>
            ))
          )}
        </HStack>
      </HStack>
    </VStack>
  );
}
